'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface NavLink {
  id: string;
  label: string;
  href: string;
  dropdown?: { label: string; href: string }[];
}

const navLinks: NavLink[] = [
  { id: 'nav_homepage', label: 'Home', href: '/homepage' },
  { id: 'nav_features', label: 'Features', href: '/features' },
  { id: 'nav_pricing', label: 'Pricing', href: '/pricing' },
  { id: 'nav_quote_builder', label: 'Quote Builder', href: '/quote-builder' },
  { id: 'nav_manual_quote', label: 'Manual Quote', href: '/manual-quote' },
  { id: 'nav_dashboard', label: 'Dashboard', href: '/dashboard' }
];


export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/login');
      router.refresh();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const isActiveLink = (link: NavLink) => {
    if (link.dropdown) {
      return link.dropdown.some(item => pathname === item.href);
    }
    return pathname === link.href;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ?
      'bg-white/90 backdrop-blur-xl shadow-sm' :
      'bg-white/70 backdrop-blur-md'}`
      }>

      <div className="container-custom px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2 md:gap-3 group">
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-primary rounded-full animate-pulse-glow" />
              <div className="w-1 h-3 md:h-4 bg-primary rounded-full" />
              <div className="w-1 h-2.5 md:h-3 bg-secondary rounded-full" />
            </div>
            <span className="text-lg sm:text-xl md:text-2xl font-heading font-bold tracking-tight group-hover:text-primary transition-colors text-[rgba(36,99,235,1)]">
              FairMarket.PRO
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center flex-1 justify-evenly max-w-4xl mx-8">
            {navLinks.map((link) => (
              link.dropdown ? (
                <div
                  key={link.id}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(link.id)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    className={`flex items-center gap-1 text-sm font-medium tracking-wide uppercase transition-all ${
                      isActiveLink(link)
                        ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {link.label}
                    <Icon name="ChevronDownIcon" size={14} variant="outline" />
                  </button>
                  
                  {openDropdown === link.id && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-xl py-2 z-50">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            pathname === item.href
                              ? 'text-primary bg-primary/10 font-medium' :'text-foreground hover:bg-primary/10'
                          }`}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.id}
                  href={link.href}
                  className={`text-sm font-medium tracking-wide uppercase transition-all ${
                    pathname === link.href
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {!loading && (
              user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-primary/10 transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold text-sm">
                      {user?.email?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-foreground">{user?.email}</span>
                    <Icon name="ChevronDownIcon" size={16} variant="outline" className="text-muted-foreground" />
                  </button>
                  
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-xl py-2">
                      <Link
                        href="/profile"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors"
                      >
                        Profile
                      </Link>
                      <Link
                        href="/dashboard"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors"
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          handleSignOut();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="text-sm font-medium text-foreground hover:text-primary transition-colors">
                    Sign In
                  </Link>
                  <Link
                    href="/signup"
                    className="px-6 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold uppercase tracking-wide hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-sm">
                    Start $1 Trial
                  </Link>
                </>
              )
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-foreground hover:text-primary transition-colors touch-manipulation"
            aria-label="Toggle menu">

            <Icon
              name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline" />

          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen &&
        <div className="md:hidden py-6 border-t border-border bg-white/95 backdrop-blur-xl max-h-[calc(100vh-5rem)] overflow-y-auto">
            <nav className="flex flex-col gap-4 px-2">
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-base font-medium tracking-wide uppercase transition-all py-2 px-3 rounded-lg touch-manipulation ${
                    pathname === link.href
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-border">
                {!loading && (
                  user ? (
                    <>
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        {user?.email}
                      </div>
                      <Link
                        href="/dashboard"
                        onClick={() => setIsMenuOpen(false)}
                        className="px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors rounded-lg hover:bg-primary/10 touch-manipulation"
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          handleSignOut();
                        }}
                        className="px-3 py-2 text-sm font-medium text-destructive hover:text-destructive/90 transition-colors text-left rounded-lg hover:bg-destructive/10 touch-manipulation"
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        href="/login"
                        onClick={() => setIsMenuOpen(false)}
                        className="px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors text-center rounded-lg hover:bg-primary/10 touch-manipulation"
                      >
                        Sign In
                      </Link>
                      <Link
                        href="/signup"
                        onClick={() => setIsMenuOpen(false)}
                        className="px-4 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold uppercase tracking-wide hover:bg-primary/90 transition-all text-center touch-manipulation"
                      >
                        Start $1 Trial
                      </Link>
                    </>
                  )
                )}
              </div>
            </nav>
          </div>
        }
      </div>
    </header>);

}